<template>
  <div>
    <v-card class="overview-risk-card light-card mb-5">
      <enterprise-risk-actions
        ref="actions"
        :disabled="riskEnterpriseLoading || noData"
        @onMinMaxChange="handleMinMaxChange"
        @onCapture="handleCapture"
      />
      <div
        class="chartWrapper"
        :class="{'is-loading': riskEnterpriseLoading}"
      >
        <template v-if="canViewData">
          <div v-if="noData && !riskEnterpriseLoading">
            <no-data-found
             clas="no-data"
            />
          </div>
          <enterprise-risk-chart
            v-if="!noData && !riskEnterpriseLoading"
            ref="chart"
            :height="500"
            :chart-data="chartData"
            @click="handleDateChange"
          />
        </template>
        <template v-else>
          <div class="no-data">
            <v-icon size="45px">info</v-icon>
            <span>
              Please fill in your billing info to enable reporting
              <router-link to="/billing">here</router-link>
            </span>
          </div>
        </template>
      </div>
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import EnterpriseRiskChart from './EnterpriseRiskChart'
import EnterpriseRiskActions from './EnterpriseRiskActions'
import { multipleByScale } from '../utils'
import NoDataFound from '@/components/Global/NoDataFound'
export default {
  name: 'EstimatedRisk',
  components: {
    EnterpriseRiskChart,
    EnterpriseRiskActions,
    NoDataFound
  },
  computed: {
    ...mapGetters('dashboardServices', ['canViewData']),
    ...mapGetters(['currentOrganization']),
    ...mapState('dashboardServices', ['riskEnterpriseData', 'riskEnterpriseLoading', 'queryScale']),
    ...mapGetters({
      colorPalette: 'colorPaletteServices/theme'
    }),
    noData () {
      return !Object.keys(this.riskEnterpriseData).length
    }
  },
  data () {
    return {
      chartData: {}
    }
  },
  async mounted () {
    this.fillData()
  },
  methods: {
    handleDateChange (index) {
      const selectedDate = this.$moment(this.riskEnterpriseData.date[index], 'YYYY-MM-DD').format('YYYY-MM-DD')
      this.$store.dispatch('dashboardServices/loadOverviewRiskData', { date: selectedDate })
      this.$store.commit('dashboardServices/SET_RISK_ENTERPRISE_DATE_INDEX', index)
    },
    fillData () {
      const minPercentage = 1
      const maxPercentage = 1
      this.chartData = {}
      // Return if the data wasn't reached
      if (!Object.keys(this.riskEnterpriseData).length) return

      const c = this.$refs?.chart?.$el.querySelector('canvas')
      if (!c) return
      const ctx = c.getContext('2d')
      console.log(ctx)

      var grd = ctx.createLinearGradient(0, 0, 0, 450)
      grd.addColorStop(0, '#DFDFE3')
      grd.addColorStop(1, '#313450')

      this.chartData = {
        labels: this.riskEnterpriseData && this.riskEnterpriseData?.date,
        datasets: [
          {
            data: (this.riskEnterpriseData instanceof Object) && this.riskEnterpriseData.data_risk_avg?.map(v => multipleByScale(+v, this.queryScale)),
            label: 'Average',
            fill: false,
            pointBorderWidth: 6,
            pointBorderColor: this.colorPalette.dotsColor,
            pointBackgroundColor: this.colorPalette.dotsColor,
            borderColor: grd,
            borderWidth: 3,
            options: {
              responsive: true,
              interaction: {
                intersect: false
              },
              scales: {
                x: {
                  display: true,
                  title: {
                    display: true
                  }
                },
                y: {
                  display: true,
                  title: {
                    display: true,
                    text: 'Value'
                  },
                  suggestedMin: -10,
                  suggestedMax: 200
                }
              }
            },
            type: 'line'
          },
          {
            data: (this.riskEnterpriseData instanceof Object) && this.riskEnterpriseData.data_risk_min?.map(v => multipleByScale(+v, this.queryScale)),
            label: 'Minimum',
            fill: '-1',
            backgroundColor: [
              this.colorPalette.chartMinimumColor
            ],
            borderColor: 'rgba(0, 0, 0, 0.0)',
            options: {
              responsive: true,
              interaction: {
                intersect: false
              },
              scales: {
                x: {
                  display: true,
                  title: {
                    display: true
                  }
                },
                y: {
                  display: true,
                  title: {
                    display: true,
                    text: 'Value'
                  },
                  suggestedMin: -10,
                  suggestedMax: 200
                }
              }
            }
          },
          {
            data: (this.riskEnterpriseData instanceof Object) && this.riskEnterpriseData.data_risk_max?.map(v => multipleByScale(+v, this.queryScale)),
            label: 'Maximum',
            backgroundColor: [
              this.colorPalette.chartMaximumColor
            ],
            borderColor: 'rgba(0, 0, 0, 0.0)',
            fill: '-1',
            options: {
              responsive: true,
              interaction: {
                intersect: false
              },
              scales: {
                x: {
                  display: true,
                  title: {
                    display: true
                  }
                },
                y: {
                  display: true,
                  title: {
                    display: true,
                    text: 'Value'
                  },
                  suggestedMin: -10,
                  suggestedMax: 200
                }
              }
            }
          }
        ]
      }
    },
    handleCapture () {
      const canvas = this.$refs?.chart?.$el.querySelector('canvas')
      if (!canvas) return
      const dataURL = canvas.toDataURL()
      const img = document.createElement('img')
      const a = document.createElement('a')
      a.href = dataURL
      a.download = 'EnterpriseDigitalRisk'
      img.onload = () => {
        document.querySelector('body').append(a)
        a.click()
        a.parentNode.removeChild(a)
      }
      img.src = dataURL
    },
    handleMinMaxChange ({ min, max, cyberDiscount }) {
      const avg = (min + max) / 2
      this.$store.commit('dashboardServices/SET_ENTERPRISE_RISK_BY_COST', { min, max, avg, cyberDiscount })
    }
  },
  watch: {
    riskEnterpriseData (newVal, oldVal) {
      if (oldVal && !Object.keys(oldVal).length && newVal && Object.keys(newVal)) {
        this.$refs.actions.handleChange(null, true)
      }
      this.fillData()
    }
  }
}
</script>

<style lang="scss">
  .estimated-risk-chart-card {
    .chartWrapper {
      position: relative;
      overflow: hidden;
      margin: 0 16px;
      &.is-loading {
        &:after {
          background: linear-gradient(90deg, transparent, rgba(108, 81, 81, 0.1), transparent);
          -webkit-animation: loading 1.5s infinite;
          animation: loading 1.5s infinite;
          content: "";
          height: 100%;
          left: 0;
          position: absolute;
          right: 0;
          top: 0;
          transform: translateX(-100%);
          z-index: 1;
        }
        canvas {
          opacity: 0;
        }
      }
    }
    margin-bottom: 23px;
  }
  .chartWrapper {
    min-height: 500px;
    position: relative;
  }
  .no-data {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
    display: inline-flex;
    flex-direction: column;
    width: fit-content;
    height: fit-content;
    span {
      color: $dark-gray-color;
      margin-top: 10px;
    }
    .v-icon {
      color: $dark-gray-color;
    }
  }
</style>
